import { useEffect, useState } from "react";
import axios from "axios";
import { fetchSponsors } from "@/lib/sponsors";
import SponsorLogo from "./SponsorLogo";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Cinematic "Presented by" title card displayed above the hero on
 * /stories/:slug and /podcast/:slug.
 *
 * Sponsor pickup priority (Phase 5G):
 *   0. Episode partner attached to this story (wins over featured).
 *   1. "Founding Partner of the Month" (featured, active) — global rotation.
 *   2. Category partner matching the story's industry.
 *   3. Rotating Founding partner (deterministic by hash of entityId).
 *   4. If still none → render nothing.
 */
export default function OpeningSponsorBand({
  variant = "story",
  preferred = [],
  preferredCategory,
  entityId = "",
}) {
  const [sponsor, setSponsor] = useState(null);
  const [featured, setFeatured] = useState(null);   // { sponsor, caption_es, landing_url, id } | null
  const [resolved, setResolved] = useState(false);

  // Reset when entity changes
  useEffect(() => {
    setSponsor(null);
    setFeatured(null);
    setResolved(false);
  }, [entityId]);

  // 1. Episode sponsor (preferred prop has the story's sponsors[] when loaded)
  useEffect(() => {
    if (sponsor || !preferred?.length) return;
    const ep = preferred.find((s) => s.tier === "episode");
    if (ep) { setSponsor(ep); setResolved(true); }
  }, [preferred, sponsor]);

  // 2/3/4. Fallback chain (featured → category → rotating founding)
  useEffect(() => {
    let cancelled = false;
    if (sponsor) { setResolved(true); return; }
    const t = setTimeout(async () => {
      if (cancelled || sponsor) return;
      try {
        // Featured of the month
        const fr = await axios.get(`${API}/api/sponsors/featured-of-the-month`);
        const f = fr.data;
        if (f && f.sponsor) {
          if (!cancelled) { setFeatured(f); setSponsor(f.sponsor); setResolved(true); }
          return;
        }
        // Category
        if (preferredCategory) {
          const cats = await fetchSponsors({ tier: "category", status: "active" });
          const match = (cats || []).find(
            (c) => (c.category || "").toLowerCase() === String(preferredCategory).toLowerCase()
          );
          if (match) { if (!cancelled) { setSponsor(match); setResolved(true); } return; }
        }
        // Rotating founding
        const founding = await fetchSponsors({ tier: "founding", status: "active" });
        if (founding && founding.length) {
          const hash = [...(entityId || "x")].reduce((a, c) => a + c.charCodeAt(0), 0);
          const pick = founding[hash % founding.length];
          if (!cancelled) { setSponsor(pick); setResolved(true); }
          return;
        }
        if (!cancelled) setResolved(true);
      } catch {
        if (!cancelled) setResolved(true);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [sponsor, preferredCategory, entityId]);

  if (!resolved || !sponsor) return null;

  const label = variant === "podcast"
    ? "Un episodio del podcast Mujeres Motor"
    : "Un episodio de Mujeres Motor";

  const isFeatured = !!featured;
  const landingHref = isFeatured
    ? (featured.landing_url || (sponsor.website || `/sponsors/${sponsor.slug}`))
    : null;

  const handleClick = () => {
    if (!isFeatured) return;
    // Phase 6.2 · GA4 featured_partner_click event
    try {
      import("@/lib/analytics").then(({ trackEvent }) =>
        trackEvent("featured_partner_click", {
          sponsor_slug: sponsor.slug,
          sponsor_tier: sponsor.tier,
        })
      );
    } catch { /* ignore */ }
    try {
      axios.post(`${API}/api/sponsors/featured-of-the-month/click`, {}, { timeout: 4000 })
        .catch(() => {});
    } catch { /* ignore */ }
  };

  const CoreBand = (
    <div
      className="relative h-full flex flex-col items-center justify-center text-center px-6 py-14 md:py-20 animate-[fadeup_900ms_ease-out_both]"
      style={{ minHeight: "35vh" }}
    >
      {isFeatured ? (
        <p
          data-testid="featured-eyebrow"
          className="text-[10px] md:text-[11px] uppercase tracking-[0.32em] text-gold mb-5"
        >
          Patrocinador del mes · Partner of the Month
        </p>
      ) : (
        <p className="text-[10px] md:text-[11px] uppercase tracking-[0.32em] text-terracotta mb-5">
          {label}
        </p>
      )}
      <div className="w-12 h-px bg-gold mb-6" />
      <p className="text-[10px] md:text-[11px] uppercase tracking-[0.36em] text-cream/65 mb-7">
        Presentado por
      </p>

      <div data-testid="opening-sponsor-logo" className="flex items-center justify-center mb-5">
        <SponsorLogo sponsor={sponsor} tone="dark" size="xl" />
      </div>

      <p className="font-serif italic text-cream/65 text-sm md:text-base max-w-lg leading-relaxed">
        {isFeatured
          ? (featured.caption_es || "— Una colaboración editorial")
          : "— Una colaboración editorial"}
      </p>
    </div>
  );

  return (
    <section
      data-testid="opening-sponsor-band"
      className="relative w-full bg-charcoal text-cream overflow-hidden"
      style={{
        minHeight: "35vh",
        background:
          "linear-gradient(180deg, #1A1A1A 0%, #131313 60%, #0E0E0E 100%)",
      }}
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.18] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.65) 100%)",
        }}
      />
      <div className="absolute top-0 left-0 right-0 h-px bg-gold/30" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-cream/8" />

      {isFeatured ? (
        <a
          href={landingHref}
          onClick={handleClick}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="featured-band-link"
          className="block"
        >
          {CoreBand}
        </a>
      ) : (
        CoreBand
      )}

      <style>{`
        @keyframes fadeup { 0% { opacity: 0; transform: translateY(14px); } 100% { opacity: 1; transform: translateY(0); } }
      `}</style>
    </section>
  );
}
